import { useEffect, useRef, type RefObject } from "react";
import { usePlatformStore } from "@platform/store";
import { VIDEO_HEALTH_CHECK_INTERVAL_MS } from "@core/features/video/video.constants";

/**
 * Detects when a <video> element has no decodable frames while the tile
 * state says 'video'. After 2 consecutive failed checks, force-transitions
 * the attendee to 'frozen' so the canvas fallback activates.
 *
 * This catches gradual network degradation where the Chime SDK doesn't fire
 * tileWillBePausedByDownlinkPolicy or remoteVideoSourcesDidChange — the SFU
 * is still sending a stream that Chrome can't decode, so the SDK thinks
 * everything is fine while Chrome clears the frame buffer (green screen).
 */
export function useVideoHealthCheck(
  videoRef: RefObject<HTMLVideoElement | null>,
  attendeeId: string,
  tileState: string,
) {
  const failCountRef = useRef(0);
  const setAttendeeTileState = usePlatformStore((s) => s.setAttendeeTileState);

  useEffect(() => {
    if (tileState !== 'video') {
      failCountRef.current = 0;
      return;
    }

    const id = setInterval(() => {
      const el = videoRef.current;
      if (!el) return;

      const isDead =
        el.readyState < 2 || el.videoWidth === 0 || el.videoHeight === 0;

      if (isDead) {
        failCountRef.current++;
        if (failCountRef.current >= 2) {
          setAttendeeTileState(attendeeId, 'frozen');
          failCountRef.current = 0;
        }
      } else {
        failCountRef.current = 0;
      }
    }, VIDEO_HEALTH_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(id);
      failCountRef.current = 0;
    };
  }, [tileState, attendeeId, videoRef, setAttendeeTileState]);
}
